import type {CreativeEngine} from "@cesdk/cesdk-js";
import {all, create} from "mathjs";
import {CALCULATOR_META_KEY} from "../constants.ts";

export type CalculatorOutputType = "number" | "currency" | "percent";

export type CalculatorMeta = {
    enabled: boolean;
    expression: string;
    outputType: CalculatorOutputType;
    decimals: number;
    useThousandsSeparator: boolean;
    currencySymbol: string;
    prefix: string;
    suffix: string;
    varName?: string;
};

export type ParsedCalculatorExpression = {
    hasDelimitedExpression: boolean;
    expression: string;
    delimiter: "doubleBrace" | "legacyDollar" | "brace" | null;
};

const TEXT_BLOCK_TYPE = "//ly.img.ubq/text";
const MAX_DECIMALS = 8;
const CALCULATOR_PASS_LIMIT = 5;
const RESERVED_SYMBOLS = new Set(["e", "pi", "i", "Infinity", "NaN", "true", "false", "null", "undefined"]);

export const CALCULATOR_DELIMITER_OPEN = "{{";
export const CALCULATOR_DELIMITER_CLOSE = "}}";
const LEGACY_CALCULATOR_DELIMITER_OPEN = "${";
const LEGACY_CALCULATOR_DELIMITER_CLOSE = "}$";

const math = create(all, {});
try {
    // Disable high-risk dynamic functions when evaluating user-entered expressions.
    math.import(
        {
            import: () => {
                throw new Error("Function import is disabled");
            },
            createUnit: () => {
                throw new Error("Function createUnit is disabled");
            },
            reviver: () => {
                throw new Error("Function reviver is disabled");
            }
        },
        {override: true}
    );
} catch {
    // Ignore in case function signatures differ between mathjs versions.
}

export const defaultCalculatorMeta = (): CalculatorMeta => ({
    enabled: false,
    expression: "",
    outputType: "number",
    decimals: 2,
    useThousandsSeparator: true,
    currencySymbol: "$",
    prefix: "",
    suffix: "",
    varName: ""
});

export const withCalculatorDefaults = (meta?: Partial<CalculatorMeta> | null): CalculatorMeta => {
    const base = defaultCalculatorMeta();
    if (!meta) return base;
    return {
        enabled: meta.enabled ?? base.enabled,
        expression: String(meta.expression ?? base.expression),
        outputType: normalizeOutputType(meta.outputType),
        decimals: clampDecimals(meta.decimals ?? base.decimals),
        useThousandsSeparator: meta.useThousandsSeparator ?? base.useThousandsSeparator,
        currencySymbol: String(meta.currencySymbol ?? base.currencySymbol),
        prefix: String(meta.prefix ?? base.prefix),
        suffix: String(meta.suffix ?? base.suffix),
        varName: String(meta.varName ?? base.varName)
    };
};

export const isTextBlock = (engine: CreativeEngine, blockId: number): boolean => {
    try {
        return engine.block.getType?.(blockId) === TEXT_BLOCK_TYPE;
    } catch {
        return false;
    }
};

export const getTextBlockText = (engine: CreativeEngine, blockId: number): string => {
    try {
        return String(engine.block.getString?.(blockId, "text/text") ?? "");
    } catch {
        return "";
    }
};

export const setTextBlockText = (engine: CreativeEngine, blockId: number, value: string): void => {
    try {
        engine.block.setString?.(blockId, "text/text", String(value ?? ""));
    } catch {}
};

export const parseDelimitedCalculatorExpression = (text: string): ParsedCalculatorExpression => {
    const trimmed = String(text ?? "").trim();
    if (!trimmed) {
        return {hasDelimitedExpression: false, expression: "", delimiter: null};
    }

    if (
        trimmed.startsWith(CALCULATOR_DELIMITER_OPEN) &&
        trimmed.endsWith(CALCULATOR_DELIMITER_CLOSE) &&
        trimmed.length >= CALCULATOR_DELIMITER_OPEN.length + CALCULATOR_DELIMITER_CLOSE.length
    ) {
        const inner = trimmed
            .slice(CALCULATOR_DELIMITER_OPEN.length, trimmed.length - CALCULATOR_DELIMITER_CLOSE.length)
            .trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "doubleBrace"};
    }

    if (
        trimmed.startsWith(LEGACY_CALCULATOR_DELIMITER_OPEN) &&
        trimmed.endsWith(LEGACY_CALCULATOR_DELIMITER_CLOSE) &&
        trimmed.length >= LEGACY_CALCULATOR_DELIMITER_OPEN.length + LEGACY_CALCULATOR_DELIMITER_CLOSE.length
    ) {
        const inner = trimmed
            .slice(LEGACY_CALCULATOR_DELIMITER_OPEN.length, trimmed.length - LEGACY_CALCULATOR_DELIMITER_CLOSE.length)
            .trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "legacyDollar"};
    }

    if (trimmed.startsWith("{") && trimmed.endsWith("}") && trimmed.length >= 2) {
        const inner = trimmed.slice(1, -1).trim();
        return {hasDelimitedExpression: true, expression: inner, delimiter: "brace"};
    }

    return {hasDelimitedExpression: false, expression: "", delimiter: null};
};

export const wrapCalculatorExpression = (expression: string): string =>
    `${CALCULATOR_DELIMITER_OPEN}${String(expression ?? "").trim()}${CALCULATOR_DELIMITER_CLOSE}`;

export const getCalculatorMeta = (engine: CreativeEngine, blockId: number): CalculatorMeta | null => {
    let raw = "";
    try {
        raw = engine.block.getMetadata?.(blockId, CALCULATOR_META_KEY) ?? "";
    } catch {
        raw = "";
    }
    if (!raw) return null;
    try {
        return withCalculatorDefaults(JSON.parse(raw));
    } catch {
        return null;
    }
};

export const setCalculatorMeta = (engine: CreativeEngine, blockId: number, meta: CalculatorMeta): void => {
    try {
        engine.block.setMetadata?.(blockId, CALCULATOR_META_KEY, JSON.stringify(withCalculatorDefaults(meta)));
    } catch {}
};

type EvaluationResult = {
    ok: boolean;
    display: string;
    rawValue: string;
};

export type CalculatorEvaluationResult = EvaluationResult;

export const evaluateCalculatorsForPage = (engine: CreativeEngine, pageId: number | null | undefined): void => {
    if (!engine || !pageId || !engine.block.isValid?.(pageId)) return;

    const blocks = findCalculatorBlocksForPage(engine, pageId);
    if (blocks.length === 0) return;

    const displayByBlock = new Map<number, string>();

    for (let pass = 0; pass < CALCULATOR_PASS_LIMIT; pass += 1) {
        let anyVarChanged = false;

        for (const blockId of blocks) {
            const meta = withCalculatorDefaults(getCalculatorMeta(engine, blockId));
            const expression = resolveExpressionForBlock(engine, blockId, meta);
            const varName = String(meta.varName ?? "").trim();

            if (!expression) {
                if (varName) {
                    const key = `calc:${varName}`;
                    const previous = readVariableByKey(engine, key);
                    if (previous !== "") {
                        try {
                            engine.variable?.setString?.(key, "");
                            anyVarChanged = true;
                        } catch {}
                    }
                }
                continue;
            }

            const result = evaluateCalculatorExpression(engine, expression, meta);
            if (!result.ok) {
                // Keep dependent calculations from using stale values after expression errors.
                if (varName) {
                    const key = `calc:${varName}`;
                    const previous = readVariableByKey(engine, key);
                    if (previous !== "") {
                        try {
                            engine.variable?.setString?.(key, "");
                            anyVarChanged = true;
                        } catch {}
                    }
                }
                continue;
            }

            displayByBlock.set(blockId, result.display);

            if (!varName) continue;

            const key = `calc:${varName}`;
            const previous = readVariableByKey(engine, key);
            if (previous !== result.rawValue) {
                try {
                    engine.variable?.setString?.(key, result.rawValue);
                    anyVarChanged = true;
                } catch {}
            }
        }

        if (!anyVarChanged) break;
    }

    displayByBlock.forEach((display, blockId) => {
        const current = getTextBlockText(engine, blockId);
        if (current !== display) {
            setTextBlockText(engine, blockId, display);
        }
    });
};

export const evaluateCalculatorForExpression = (
    engine: CreativeEngine,
    expressionRaw: string,
    meta?: Partial<CalculatorMeta> | null
): CalculatorEvaluationResult => {
    const normalizedMeta = withCalculatorDefaults(meta ?? undefined);
    return evaluateCalculatorExpression(engine, expressionRaw, normalizedMeta);
};

const findCalculatorBlocksForPage = (engine: CreativeEngine, pageId: number): number[] => {
    const out: number[] = [];
    const stack: number[] = [pageId];

    while (stack.length > 0) {
        const id = stack.pop()!;
        if (!engine.block.isValid?.(id)) continue;

        if (isTextBlock(engine, id)) {
            const text = getTextBlockText(engine, id);
            const parsed = parseDelimitedCalculatorExpression(text);
            const meta = withCalculatorDefaults(getCalculatorMeta(engine, id));
            if (parsed.hasDelimitedExpression || (meta.enabled && meta.expression.trim() !== "")) {
                out.push(id);
            }
        }

        try {
            const children = engine.block.getChildren?.(id) ?? [];
            for (const childId of children) stack.push(childId);
        } catch {}

        try {
            if (engine.block.supportsFill?.(id)) {
                const fill = engine.block.getFill?.(id);
                if (fill != null) stack.push(fill);
            }
        } catch {}
    }

    return out;
};

const resolveExpressionForBlock = (engine: CreativeEngine, blockId: number, meta: CalculatorMeta): string => {
    const text = getTextBlockText(engine, blockId);
    const parsed = parseDelimitedCalculatorExpression(text);

    if (parsed.hasDelimitedExpression) {
        if (!meta.enabled || meta.expression !== parsed.expression) {
            setCalculatorMeta(engine, blockId, {
                ...meta,
                enabled: true,
                expression: parsed.expression
            });
        }
        return parsed.expression;
    }

    if (meta.enabled && meta.expression.trim() !== "") {
        return meta.expression;
    }

    return "";
};

const evaluateCalculatorExpression = (engine: CreativeEngine, expressionRaw: string, meta: CalculatorMeta): EvaluationResult => {
    const expression = normalizeExpression(expressionRaw);
    if (!expression) {
        return {ok: false, display: "", rawValue: ""};
    }

    const identifiers = extractSymbols(expression);
    const scope: Record<string, unknown> = {
        v: (name: string) => coerceToNumber(readVariable(engine, String(name ?? "").trim())),
        value: (name: string) => coerceToNumber(readVariable(engine, String(name ?? "").trim()))
    };

    identifiers.forEach((name) => {
        scope[name] = coerceToNumber(readVariable(engine, name));
    });

    let evaluated: unknown;
    try {
        evaluated = math.evaluate(expression, scope);
    } catch {
        return {ok: false, display: "", rawValue: ""};
    }

    const numeric = typeof evaluated === "number" ? evaluated : Number(evaluated);
    const hasNumeric = Number.isFinite(numeric);
    const rawValue = hasNumeric ? String(numeric) : String(evaluated ?? "");
    const display = formatCalculatorValue(hasNumeric ? numeric : evaluated, meta);

    return {ok: true, display, rawValue};
};

const LOOSE_OPERATOR_SPLIT = /\s([+\-*/%^])\s/;
const NUMERIC_LITERAL_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;

const escapeMathStringLiteral = (value: string): string =>
    String(value ?? "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');

const isLikelyLooseBracketExpression = (value: string): boolean => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return false;
    if (!LOOSE_OPERATOR_SPLIT.test(trimmed)) return false;

    const parts = trimmed.split(LOOSE_OPERATOR_SPLIT);
    if (parts.length < 3) return false;

    const operands = parts
        .filter((_part, index) => index % 2 === 0)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

    return operands.some((operand) => NUMERIC_LITERAL_PATTERN.test(operand));
};

const normalizeLooseBracketExpression = (value: string): string => {
    const parts = String(value ?? "").trim().split(LOOSE_OPERATOR_SPLIT);
    return parts
        .map((part, index) => {
            if (index % 2 === 1) {
                return String(part ?? "").trim();
            }
            const operand = String(part ?? "").trim();
            if (!operand) return "0";
            if (NUMERIC_LITERAL_PATTERN.test(operand)) return operand;
            if (/[()]/.test(operand)) return operand;
            return `v("${escapeMathStringLiteral(operand)}")`;
        })
        .join(" ");
};

const normalizeExpression = (rawExpression: string): string => {
    const trimmed = String(rawExpression ?? "").trim();
    if (!trimmed) return "";
    const parsed = parseDelimitedCalculatorExpression(trimmed);
    const unwrapped = parsed.hasDelimitedExpression ? parsed.expression : trimmed;
    if (!unwrapped) return "";
    return unwrapped.replace(/\[([^\]]+)\]/g, (_match, token) => {
        const key = String(token ?? "").trim();
        if (!key) return "0";
        if (isLikelyLooseBracketExpression(key)) {
            return normalizeLooseBracketExpression(key);
        }
        return `v("${escapeMathStringLiteral(key)}")`;
    });
};

const extractSymbols = (expression: string): string[] => {
    const names = new Set<string>();
    try {
        const parsed = math.parse(expression);
        parsed.traverse((node: any, _path: string, parent: any) => {
            if (!node?.isSymbolNode) return;
            const name = String(node.name ?? "").trim();
            if (!name) return;
            if (RESERVED_SYMBOLS.has(name)) return;
            // If this symbol is the function name in a call, keep it out of the variable scope.
            if (parent?.isFunctionNode && parent.fn === node) return;
            names.add(name);
        });
    } catch {
        return [];
    }
    return Array.from(names);
};

const readVariableByKey = (engine: CreativeEngine, key: string): string => {
    try {
        return String(engine.variable?.getString?.(key) ?? "");
    } catch {
        return "";
    }
};

const readVariable = (engine: CreativeEngine, name: string): string => {
    if (!name) return "";
    const candidates = [
        `input:${name}`,
        `calc:${name}`,
        `slider:${name}:value`,
        name
    ];
    for (const key of candidates) {
        const value = readVariableByKey(engine, key);
        if (value.trim() !== "") {
            return value;
        }
    }
    return "";
};

const coerceToNumber = (raw: unknown): number => {
    if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
    if (typeof raw !== "string") return 0;
    const direct = Number(raw);
    if (Number.isFinite(direct)) return direct;
    const cleaned = raw.replace(/[^0-9.\-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeOutputType = (value: unknown): CalculatorOutputType => {
    if (value === "currency" || value === "percent" || value === "number") {
        return value;
    }
    return "number";
};

const formatCalculatorValue = (result: unknown, meta: CalculatorMeta): string => {
    const outputType = normalizeOutputType(meta.outputType);
    const decimals = clampDecimals(meta.decimals);
    const useGrouping = meta.useThousandsSeparator !== false;

    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping
    });

    const numeric = typeof result === "number" ? result : Number(result);
    const hasNumeric = Number.isFinite(numeric);

    let value = "";
    if (hasNumeric) {
        if (outputType === "percent") {
            value = `${formatter.format(numeric * 100)}%`;
        } else if (outputType === "currency") {
            const symbol = meta.currencySymbol || "$";
            value = `${symbol}${formatter.format(numeric)}`;
        } else {
            value = formatter.format(numeric);
        }
    } else {
        value = "";
    }

    const prefix = meta.prefix ?? "";
    const suffix = meta.suffix ?? "";
    return `${prefix}${value}${suffix}`;
};

const clampDecimals = (value: number): number => {
    if (!Number.isFinite(value)) return 2;
    return Math.max(0, Math.min(MAX_DECIMALS, Math.round(value)));
};
